import { combineReducers } from 'redux';
import { ActionType, createAppCacheReducer } from '@fullstory/redux-util/utils';
import { SessionReplayFilterActions } from './actions';
import { defaultCardFilters } from './selectors';
import {
  defaultSessionPlaylistCardFilterState,
  SessionPlaylistCardFilterActionTypes,
  SessionPlaylistCardFilterState,
  SessionReplayTypes,
  UsersAndSessions,
} from './types';
import { isNumber } from 'lodash';

const usersAndSessions = createAppCacheReducer<UsersAndSessions>()(
  SessionReplayTypes.UsersAppCacheIsLoading,
  SessionReplayTypes.UsersAppCacheLoadFailed,
  SessionReplayTypes.UsersAppCacheRefetching,
  SessionReplayTypes.UsersAppCacheHasData,
  SessionReplayTypes.UsersAppCacheFresh,
  SessionReplayTypes.UsersAppCacheSwap,
  SessionReplayTypes.UsersAppCacheClear,
)();

export function sessionPlaylistCardFilterReducer(
  state: SessionPlaylistCardFilterState = defaultSessionPlaylistCardFilterState,
  action: ActionType<typeof SessionReplayFilterActions>,
): SessionPlaylistCardFilterState {
  switch (action.type) {
    case SessionPlaylistCardFilterActionTypes.SetActiveSegmentId: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, activeSegmentId: action.activeSegmentId },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetDimension: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, dimension: action.dimension },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetFunnelConverted: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, funnelConverted: action.converted },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetFunnelStep: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, funnelStep: action.step },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetPage: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: {
          ...prevFilterState,
          page: action.page,
          queryPage: isNumber(action.queryPage) ? action.queryPage : action.page,
        },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetEventIndex: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, eventIndex: action.eventIndex },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetReturnEventIndex: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, returnEventIndex: action.returnEventIndex },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetCohortIndex: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, cohortIndex: action.cohortIndex },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetReturnWindowIndex: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, returnWindowIndex: action.returnWindowIndex },
      };
    }
    case SessionPlaylistCardFilterActionTypes.SetCompleteSessions: {
      const prevFilterState = state[action.storageId] ?? defaultCardFilters;
      return {
        ...state,
        [action.storageId]: { ...prevFilterState, completeSessions: action.completeSessions },
      };
    }
    case SessionPlaylistCardFilterActionTypes.ClearFilters:
      return {
        ...state,
        [action.storageId]: {
          ...state[action.storageId],
          ...defaultCardFilters,
        },
      };
    default:
      return state;
  }
}

type SessionReplayState = {
  usersAndSessions: ReturnType<typeof usersAndSessions>;
  sessionPlaylistCardFilterReducer: ReturnType<typeof sessionPlaylistCardFilterReducer>;
};

export const sessionReplay = combineReducers<SessionReplayState>({
  usersAndSessions,
  sessionPlaylistCardFilterReducer,
});
